import { Padder } from '@oy/uidesktop';
import { IS_DANARAPAY } from 'src/constants';
import { useAccountApproved, useDataActivation } from 'src/hooks';
import { DevConfigBanner } from '../DevConfigBanner';
import { ErrorBoundary } from '../ErrorBoundary';
import { UserMenu } from '../UserMenu';
import { WithdrawalModal } from '../WithdrawalModal';
import {
  ApiKeyMenuItem,
  BankAccountSection,
  DevConfigMenuItem,
  EnvSwitcher,
  MenuDropdown,
  MenuItem,
  SectionTitle,
  SidebarLogo,
  SidebarWrapper,
} from './components';
import { BalanceSection } from './components/BalanceSection/BalanceSection';
import styles from './components/Sidebar.module.css';
import { SideBarItems } from './constants';
import { MenuItem as MenuItemType } from './type';
import { useSidebarImpl } from './useSidebarImpl';

/**
 * This Component will render sidebar menu item recursively
 *
 * @returns generated dropdown and menu item component
 */
const RenderedMenuDropdown = (
  props: MenuItemType & {
    routeIsActive: (path: string) => boolean | undefined;
    showProductGroup: (title: string) => boolean;
  },
) => {
  const { children, routeIsActive, showProductGroup, ...menuProps } = props;

  if (!showProductGroup(menuProps.title) && !routeIsActive(menuProps.path))
    return null;
  return children && children.length > 0 ? (
    <MenuDropdown {...props} firstChildPath={children[0].path}>
      {children.map(subMenuItem => (
        <RenderedMenuDropdown
          key={subMenuItem.path}
          {...subMenuItem}
          routeIsActive={routeIsActive}
          showProductGroup={showProductGroup}
        />
      ))}
    </MenuDropdown>
  ) : (
    <MenuItem {...menuProps} />
  );
};

/**
 * Main Sidebar Component
 */
const Sidebar = () => {
  const { isShowSection, isShowProduct, routeIsActive, state } =
    useSidebarImpl();
  const { isProdEnv } = state;

  const { result, data, isLoading, isValidating } = useDataActivation(false);
  const isFetchData = isLoading || isValidating || !data;
  const isOverbookingActive = result?.get('OVERBOOKING')?.status === 'ACTIVE';
  const isSOFActive = result?.get('SOURCE_OF_FUND')?.status === 'ACTIVE';

  const isNewFlow = isOverbookingActive || isSOFActive;
  const isAccountApproved = useAccountApproved();

  return (
    <SidebarWrapper>
      <Padder vertical={Padder.Size.M} horizontal={Padder.Size.XXXS}>
        <SidebarLogo />
        <DevConfigBanner />
        <div className={styles.sidebarTopSection}>
          {!isFetchData && isNewFlow && isProdEnv && <BankAccountSection />}
          {(!isProdEnv ||
            (isAccountApproved && !isFetchData && !isNewFlow)) && (
            <BalanceSection />
          )}

          {!IS_DANARAPAY && (
            <div className={styles.envButton}>
              <EnvSwitcher />
            </div>
          )}
        </div>
      </Padder>
      <nav>
        <DevConfigMenuItem />
        {SideBarItems().map(section => {
          if (!isShowSection(section.title)) return;
          return [
            section.title && (
              <SectionTitle
                key={section.title}
                title={section.title}
                isProdEnv={isProdEnv}
              />
            ),
            section.children.map(menuItems => (
              <RenderedMenuDropdown
                key={menuItems.path}
                {...menuItems}
                routeIsActive={routeIsActive}
                showProductGroup={isShowProduct}
              />
            )),
          ];
        })}
      </nav>
      <ApiKeyMenuItem />
      {!isFetchData && !isNewFlow && (
        <ErrorBoundary domain="dashboard" componentName="WithdrawalModal">
          <WithdrawalModal />
        </ErrorBoundary>
      )}

      <UserMenu />
    </SidebarWrapper>
  );
};

export default Sidebar;
